/* eslint-disable no-return-assign */
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import MixPanel from 'utils/mixpanelService';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Divider from '@material-ui/core/Divider';
import withSizes from 'react-sizes';
import ReactTooltip from 'react-tooltip';
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  PinterestShareButton,
} from 'react-share';
import ClassNames from 'classnames';
import { fallbackImage, getParentShareUrl } from 'utils/stringUtils';
import LinkIcon from '@material-ui/icons/Link';
import ButtonBase from '@material-ui/core/ButtonBase';
import ShareTransparentIcon from './images/share-transparent.svg';
import ShareIcon from './images/share.svg';
import FBIcon from './images/facebook.svg';
import TwitterIcon from './images/twitter.svg';
import EmailIcon from './images/email.svg';
import LinkedInIcon from './images/linkedin.svg';
import PinterestIcon from './images/pinterest.svg';

const styles = theme => ({
  paper: {
    boxShadow: 'none',
    borderRadius: '3px !important',
    border: '1px solid #E3E3E3',
  },
  withShadow: {
    boxShadow:
      '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)',
  },
  buttonRoot: {
    color: theme.palette.text.primary,
    padding: 0,
    maxWidth: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  isButton: {
    width: '100%',
  },
  popperContainer: {
    zIndex: 999,
  },
  inverseText: {
    color: theme.palette.text.primary,
  },
  shareIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
  },
  label: {
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: 8,
  },
  divider: {
    margin: '0 8%',
  },
  listItem: {
    '& > div': {
      display: 'flex',
    },
  },
  buttonWrapper: {
    width: '100%',
    border: '1px solid #E3E3E3',
    borderRadius: 5,
    height: 48,
    display: 'flex',
  },
  whiteBtn: {
    background: '#ffff',
  },
  linkIcon: {
    fontSize: 20,
    marginRight: 5,
  },
  linkBtn: {
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: 'MadaRegular',
    color: 'rgb(75, 75, 75)',
    '& input': {
      position: 'absolute',
      top: '-999999999px',
    },
  },
});

class Share extends React.Component {
  state = {
    open: false,
  };

  handleClick = social => {
    const { name, type } = this.props;
    MixPanel.track(`Sharing Resource`, {
      name,
      type,
      social,
    });
  };

  handleToggle = () => {
    this.setState(state => ({ open: !state.open }));
  };

  handleClose = event => {
    if (this.anchorEl.contains(event.target)) {
      return;
    }
    this.setState({ open: false });
  };

  getLabel = label => {
    const { classes } = this.props;
    return <Typography className={classes.label}>{label}</Typography>;
  };

  getIconBtnContent = () => {
    const {
      mobileShare,
      classes,
      isAdmin,
      iconOnly,
      inverseText,
      isButton,
    } = this.props;
    const { open } = this.state;
    return (
      <IconButton
        buttonRef={node => {
          this.anchorEl = node;
        }}
        aria-owns={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={this.handleToggle}
        className={ClassNames(
          classes.buttonRoot,
          inverseText ? classes.inverseText : undefined,
          isButton ? classes.isButton : undefined,
        )}
        disableRipple
        data-tip
        data-for="share"
        arial-label="Share"
      >
        <img
          src={
            (mobileShare || isAdmin) && !iconOnly && !inverseText
              ? ShareTransparentIcon
              : ShareIcon
          }
          className={classes.shareIcon}
          alt="Share"
        />
        {!mobileShare && !iconOnly && !isAdmin && (
          <Typography variant="overline">Share</Typography>
        )}
      </IconButton>
    );
  };

  getIconBtn = () => {
    const { mobileShare, isAdmin, iconOnly, isButton, classes } = this.props;
    const btnContent = this.getIconBtnContent();
    const isBestResource = window.location.pathname.includes('/bestlists/');
    if (mobileShare || isAdmin || iconOnly) {
      return (
        <div
          className={ClassNames(
            isButton && classes.buttonWrapper,
            isBestResource && classes.whiteBtn,
          )}
        >
          <ReactTooltip id="share" place="top" type="light" border>
            Share
          </ReactTooltip>
          {btnContent}
        </div>
      );
    }
    return btnContent;
  };

  handleCopyLink = () => {
    this.linkRef.select();
    document.execCommand('copy');
  };

  render() {
    const {
      classes,
      name,
      inverseText,
      withLinkedin,
      withPinterest,
    } = this.props;
    const parentHref = getParentShareUrl();
    const { open } = this.state;
    const shareUrl = parentHref || window.location.href;
    const shareQuote = `${name} @CredibleMind`;
    const image = fallbackImage; // default for now since no image for editor lsit
    return (
      <>
        {this.getIconBtn()}
        <Popper
          open={open}
          anchorEl={this.anchorEl}
          className={classes.popperContainer}
          transition
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              id="menu-list-grow"
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper
                className={ClassNames(
                  classes.paper,
                  inverseText ? classes.withShadow : undefined,
                )}
              >
                <ClickAwayListener onClickAway={this.handleClose}>
                  <MenuList>
                    <MenuItem className={classes.listItem}>
                      <FacebookShareButton
                        url={shareUrl}
                        quote={shareQuote}
                        className={classes.icon}
                        beforeOnClick={() => this.handleClick('facebook')}
                      >
                        <img src={FBIcon} alt="facebook" />
                        {this.getLabel('Share on Facebook')}
                      </FacebookShareButton>
                    </MenuItem>
                    <Divider className={classes.divider} />
                    <MenuItem className={classes.listItem}>
                      <TwitterShareButton
                        url={shareUrl}
                        title={shareQuote}
                        className={classes.icon}
                        beforeOnClick={() => this.handleClick('twitter')}
                      >
                        <img src={TwitterIcon} alt="twitter" />
                        {this.getLabel('Tweet on Twitter')}
                      </TwitterShareButton>
                    </MenuItem>
                    <Divider className={classes.divider} />
                    <MenuItem className={classes.listItem}>
                      <EmailShareButton
                        url={shareUrl}
                        subject={shareQuote}
                        className={classes.icon}
                        beforeOnClick={() => this.handleClick('email')}
                      >
                        <img src={EmailIcon} alt="email" />
                        {this.getLabel('Send as Email')}
                      </EmailShareButton>
                    </MenuItem>
                    {withLinkedin && (
                      <>
                        <Divider className={classes.divider} />
                        <MenuItem className={classes.listItem}>
                          <LinkedinShareButton
                            url={shareUrl}
                            title={shareQuote}
                            beforeOnClick={() => this.handleClick('linkedin')}
                          >
                            <img src={LinkedInIcon} alt="linkedin" />
                            {this.getLabel('Share on LinkedIn')}
                          </LinkedinShareButton>
                        </MenuItem>
                      </>
                    )}
                    {withPinterest && (
                      <>
                        <Divider className={classes.divider} />
                        <MenuItem className={classes.listItem}>
                          <PinterestShareButton
                            url={String(window.location)}
                            media={image}
                            beforeOnClick={() => this.handleClick('pinterest')}
                          >
                            <img src={PinterestIcon} alt="pinterest" />
                            {this.getLabel('Share on Pinterest')}
                          </PinterestShareButton>
                        </MenuItem>
                      </>
                    )}
                    <Divider className={classes.divider} />
                    <MenuItem className={classes.listItem}>
                      <ButtonBase
                        className={classes.linkBtn}
                        onClick={this.handleCopyLink}
                      >
                        <input
                          type="text"
                          className={classes.hiddenInput}
                          ref={link => (this.linkRef = link)}
                          value={shareUrl}
                        />
                        <LinkIcon className={classes.linkIcon} />
                        Copy Link
                      </ButtonBase>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }
}

Share.propTypes = {
  classes: PropTypes.object.isRequired,
  name: PropTypes.string,
  handleClick: PropTypes.func,
  mobileShare: PropTypes.bool,
  isAdmin: PropTypes.bool,
  iconOnly: PropTypes.bool,
  inverseText: PropTypes.bool,
  type: PropTypes.string,
  withLinkedin: PropTypes.bool,
  withPinterest: PropTypes.bool,
  isButton: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  mobileShare: width < 750,
});

export default compose(
  withStyles(styles),
  withSizes(mapSizesToProps),
)(Share);
